<template>
  <!-- Teleported to <body>: `position: fixed` resolves against the nearest
       transformed ancestor, and the home page's horizontal track is
       permanently translated on X — left in place this would anchor to the
       track's origin and appear over the first panel. -->
  <Teleport to="body">
    <Transition name="unavailable-fade">
      <div v-if="project" class="unavailable-overlay" @click="$emit('close')">
        <div
          class="unavailable"
          role="alertdialog"
          aria-modal="true"
          :aria-label="heading"
          @click.stop
        >
          <button type="button" class="unavailable__close" aria-label="Close" @click="$emit('close')">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <div class="unavailable__mark">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="9" />
              <line x1="12" y1="8" x2="12" y2="13" />
              <line x1="12" y1="16.5" x2="12" y2="16.5" />
            </svg>
          </div>

          <h3 class="unavailable__title">{{ heading }}</h3>

          <p class="unavailable__body">
            <strong>{{ project.title }}</strong> has no public link to share at the moment.
            It is real work, but the build is private or offline right now. Worth
            checking back later.
          </p>

          <button type="button" class="unavailable__dismiss" @click="$emit('close')">
            Close
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
defineProps({
  // { title } while open, null when closed.
  project: { type: Object, default: null },
});

defineEmits(['close']);

const heading = 'Not available yet';
</script>

<style lang="scss" scoped>
.unavailable-overlay {
  position: fixed;
  inset: 0;
  z-index: 3200;
  display: flex;
  justify-content: center;
  // Centred by the card's auto margin, so a card taller than the screen can
  // still be scrolled to its top.
  overflow-y: auto;
  padding: 1.5rem;
  background: rgba(0, 0, 0, 0.5);
}

.unavailable {
  position: relative;
  margin: auto;
  width: 100%;
  max-width: 380px;
  padding: 2rem 1.75rem 1.5rem;
  border-radius: 18px;
  border: 1px solid rgba(var(--border-rgb), 0.4);
  background: var(--background);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.2);
  text-align: center;
}

.unavailable__close {
  position: absolute;
  top: 0.8rem;
  right: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 50%;
  background: rgba(var(--muted-rgb), 0.12);
  color: var(--text);
  cursor: pointer;
  transition: background 0.2s ease-in-out;

  &:hover {
    background: rgba(var(--muted-rgb), 0.22);
  }
}

.unavailable__mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin: 0 auto 0.9rem;
  border-radius: 50%;
  background: rgba(var(--muted-rgb), 0.1);
  color: var(--muted);
}

.unavailable__title {
  margin: 0 0 0.55rem;
  font-size: 1.02rem;
  font-weight: 700;
  color: var(--text);
}

.unavailable__body {
  margin: 0;
  font-size: 0.86rem;
  line-height: 1.6;
  color: var(--secondary);

  strong {
    color: var(--text);
  }
}

.unavailable__dismiss {
  margin-top: 1.35rem;
  padding: 0.45rem 1.2rem;
  border-radius: 999px;
  border: none;
  background: rgba(var(--muted-rgb), 0.12);
  font: inherit;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text);
  cursor: pointer;
  transition: background 0.2s ease-in-out;

  &:hover {
    background: rgba(var(--muted-rgb), 0.2);
  }
}

.unavailable-fade-enter-active,
.unavailable-fade-leave-active {
  transition: opacity 0.22s ease;

  .unavailable {
    transition: transform 0.22s ease;
  }
}

.unavailable-fade-enter-from,
.unavailable-fade-leave-to {
  opacity: 0;

  .unavailable {
    transform: translateY(8px) scale(0.97);
  }
}
</style>
